import React, { createContext, useContext, useEffect, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Collapse, Table, Input, InputNumber, Button, Popconfirm, Typography, message, Spin } from 'antd';
import { PlusOutlined, DeleteOutlined, SaveOutlined, HolderOutlined } from '@ant-design/icons';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getCatalogueStandards } from '../../graphql/queries';
import { updateCatalogueStandards } from '../../graphql/mutation';

const { Title, Paragraph } = Typography;

const EMPTY_STANDARDS = { durations: [], resolutions: [], ratios: [] };

// Rows carry a stable `id` once saved (assigned by the server); a row added
// this session gets a temporary client-side one so drag-and-drop and the
// table both have something stable to key on before the first save.
const getRowKey = (row) => row.id || row._clientId;

const validateList = (rows, listLabel) => {
  const keys = new Set();
  for (const row of rows) {
    const key = (row.key || '').trim();
    if (!key) {
      message.error(`${listLabel}: every row needs a non-empty key.`);
      return false;
    }
    if (keys.has(key)) {
      message.error(`${listLabel}: key "${key}" is used more than once.`);
      return false;
    }
    keys.add(key);
  }
  return true;
};

// Hands the row's drag listeners down to just the handle cell, so dragging
// only starts from the handle icon — not from anywhere in the row (which
// would fight with clicking into the Input/InputNumber cells).
const RowDragContext = createContext(null);

const DragHandle = () => {
  const { listeners, setActivatorNodeRef } = useContext(RowDragContext) || {};
  return (
    <HolderOutlined
      ref={setActivatorNodeRef}
      style={{ cursor: 'grab', color: '#999', touchAction: 'none' }}
      {...listeners}
    />
  );
};

const DraggableRow = ({ children, ...props }) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id: props['data-row-key'] });

  const style = {
    ...props.style,
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9999, background: '#fafafa' } : {}),
  };

  return (
    <RowDragContext.Provider value={{ listeners, setActivatorNodeRef }}>
      <tr {...props} {...attributes} ref={setNodeRef} style={style}>
        {children}
      </tr>
    </RowDragContext.Provider>
  );
};

// Rendered as one panel of the accordion below. `key` order in `rows` IS the
// order model configs get sorted to on save — dragging a row reorders it.
const useStandardsPanel = ({ key, title, description, rows, setRows, columns, emptyRow }) => {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = rows.findIndex((r) => getRowKey(r) === active.id);
    const newIndex = rows.findIndex((r) => getRowKey(r) === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    setRows(arrayMove(rows, oldIndex, newIndex));
  };

  return {
    key,
    label: <span style={{ fontSize: 15, fontWeight: 600 }}>{title}{rows?.length ? ` (${rows.length})` : ''}</span>,
    extra: (
      <Button
        type="dashed"
        size="small"
        icon={<PlusOutlined />}
        onClick={(e) => {
          e.stopPropagation();
          setRows([...(rows || []), { ...emptyRow, _isNew: true, _clientId: `new-${crypto.randomUUID()}` }]);
        }}
      >
        Add
      </Button>
    ),
    children: (
      <>
        {description && <Paragraph type="secondary" style={{ marginTop: -8 }}>{description}</Paragraph>}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={rows.map(getRowKey)} strategy={verticalListSortingStrategy}>
            <Table
              dataSource={rows}
              rowKey={getRowKey}
              pagination={false}
              size="small"
              columns={columns}
              locale={{ emptyText: 'No standards defined yet' }}
              components={{ body: { row: DraggableRow } }}
            />
          </SortableContext>
        </DndContext>
      </>
    ),
  };
};

export const CatalogueStandards = () => {
  const [standards, setStandards] = useState(EMPTY_STANDARDS);
  const { data, loading, refetch } = useQuery(getCatalogueStandards);
  const [updateMutation, { loading: saving }] = useMutation(updateCatalogueStandards);

  useEffect(() => {
    if (data?.getCatalogueStandards) {
      const raw = JSON.parse(JSON.stringify(data.getCatalogueStandards));
      setStandards({
        durations: raw.durations || [],
        resolutions: raw.resolutions || [],
        ratios: raw.ratios || [],
      });
    }
  }, [data]);

  const updateRow = (listName, index, field, value) => {
    setStandards((prev) => {
      const rows = JSON.parse(JSON.stringify(prev[listName]));
      rows[index][field] = value;
      return { ...prev, [listName]: rows };
    });
  };

  const removeRow = (listName, index) => {
    setStandards((prev) => ({
      ...prev,
      [listName]: prev[listName].filter((_, i) => i !== index),
    }));
  };

  const dragHandleColumn = () => ({
    key: 'drag',
    width: 32,
    render: () => <DragHandle />,
  });

  const keyColumn = (listName) => ({
    title: 'Key',
    key: 'key',
    width: 160,
    render: (_, row, i) => (
      <Input
        size="small"
        placeholder="e.g. 6s"
        value={row.key}
        onChange={(e) => updateRow(listName, i, 'key', e.target.value)}
      />
    ),
  });

  const labelColumn = (listName) => ({
    title: 'Label',
    key: 'label',
    width: 220,
    render: (_, row, i) => (
      <Input
        size="small"
        placeholder="e.g. 6 seconds"
        value={row.label}
        onChange={(e) => updateRow(listName, i, 'label', e.target.value)}
      />
    ),
  });

  const actionColumn = (listName) => ({
    title: 'Action',
    key: 'action',
    width: 60,
    render: (_, __, i) => (
      <Popconfirm
        title="Remove standard"
        description="If this key is still used by a model, the save will be rejected."
        onConfirm={() => removeRow(listName, i)}
        okText="Yes"
        cancelText="No"
      >
        <Button danger size="small" type="text" icon={<DeleteOutlined />} />
      </Popconfirm>
    ),
  });

  const durationColumns = [
    dragHandleColumn(),
    keyColumn('durations'),
    labelColumn('durations'),
    {
      title: 'Short Name',
      key: 'short_name',
      width: 140,
      render: (_, row, i) => (
        <Input
          size="small"
          placeholder="e.g. 6s"
          value={row.short_name}
          onChange={(e) => updateRow('durations', i, 'short_name', e.target.value)}
        />
      ),
    },
    {
      title: 'Seconds',
      key: 'seconds',
      width: 120,
      render: (_, row, i) => (
        <InputNumber
          size="small"
          min={0}
          style={{ width: '100%' }}
          value={row.seconds}
          onChange={(val) => updateRow('durations', i, 'seconds', val)}
        />
      ),
    },
    actionColumn('durations'),
  ];

  const resolutionColumns = [
    dragHandleColumn(),
    keyColumn('resolutions'),
    labelColumn('resolutions'),
    actionColumn('resolutions'),
  ];

  const ratioColumns = [
    dragHandleColumn(),
    keyColumn('ratios'),
    labelColumn('ratios'),
    {
      title: 'Short Name',
      key: 'short_name',
      width: 140,
      render: (_, row, i) => (
        <Input
          size="small"
          placeholder="e.g. 16:9"
          value={row.short_name}
          onChange={(e) => updateRow('ratios', i, 'short_name', e.target.value)}
        />
      ),
    },
    {
      title: 'Aspect X',
      key: 'aspect_ratio_x',
      width: 110,
      render: (_, row, i) => (
        <InputNumber
          size="small"
          min={0}
          placeholder="auto"
          style={{ width: '100%' }}
          value={row.aspect_ratio_x}
          onChange={(val) => updateRow('ratios', i, 'aspect_ratio_x', val)}
        />
      ),
    },
    {
      title: 'Aspect Y',
      key: 'aspect_ratio_y',
      width: 110,
      render: (_, row, i) => (
        <InputNumber
          size="small"
          min={0}
          placeholder="auto"
          style={{ width: '100%' }}
          value={row.aspect_ratio_y}
          onChange={(val) => updateRow('ratios', i, 'aspect_ratio_y', val)}
        />
      ),
    },
    actionColumn('ratios'),
  ];

  const durationsPanel = useStandardsPanel({
    key: 'durations',
    title: 'Durations',
    description: 'Used by video models.',
    rows: standards.durations,
    setRows: (rows) => setStandards((prev) => ({ ...prev, durations: rows })),
    columns: durationColumns,
    emptyRow: { key: '', label: '', short_name: '', seconds: null },
  });

  const resolutionsPanel = useStandardsPanel({
    key: 'resolutions',
    title: 'Resolutions',
    description: 'Used by video and image models.',
    rows: standards.resolutions,
    setRows: (rows) => setStandards((prev) => ({ ...prev, resolutions: rows })),
    columns: resolutionColumns,
    emptyRow: { key: '', label: '' },
  });

  const ratiosPanel = useStandardsPanel({
    key: 'ratios',
    title: 'Ratios',
    description: 'Used by video and image models (crop / text-to-media / image-to-media ratio lists).',
    rows: standards.ratios,
    setRows: (rows) => setStandards((prev) => ({ ...prev, ratios: rows })),
    columns: ratioColumns,
    emptyRow: { key: '', label: '', short_name: '', aspect_ratio_x: null, aspect_ratio_y: null },
  });

  const handleSave = async () => {
    if (!validateList(standards.durations, 'Durations')) return;
    if (!validateList(standards.resolutions, 'Resolutions')) return;
    if (!validateList(standards.ratios, 'Ratios')) return;

    const strip = (rows) => rows.map(({ _isNew, _clientId, ...rest }) => rest);
    const payload = {
      durations: strip(standards.durations),
      resolutions: strip(standards.resolutions),
      ratios: strip(standards.ratios),
    };

    try {
      await updateMutation({ variables: { standardsJson: payload } });
      message.success('Catalogue standards saved.');
      await refetch();
    } catch (err) {
      message.error(err.message || 'Failed to save standards');
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Spin size="large" />
        <p style={{ marginTop: 16 }}>Loading standards...</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1200px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <Title level={2} style={{ color: 'black', margin: 0 }}>Catalogue Standards</Title>
        <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
          Save Changes
        </Button>
      </div>
      <Paragraph type="secondary">
        Shared duration/resolution/ratio keys used across model configs.
      </Paragraph>

      <Collapse
        defaultActiveKey={['durations', 'resolutions', 'ratios']}
        items={[durationsPanel, resolutionsPanel, ratiosPanel]}
      />
    </div>
  );
};

export default CatalogueStandards;
